import './App.css';
import { Box, CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LoginCallback } from '@okta/okta-react';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { cloneElement, ReactElement, useEffect, useRef } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import AddOrganization from './components/features/admin/AddOrganization';
import OrganizationDetails from './components/features/admin/OrganizationDetails';
import OrganizationManagement from './components/features/admin/OrganizationManagement';
import AllocationsGroupList from './components/features/AllocationsGroupList';
import AuditLogsList from './components/features/AuditLogsList';
import BudgetGroupsList from './components/features/BudgetGroupsList';
import DistributorsList from './components/features/DistributorsList';
import EditAllocationGroup from './components/features/EditAllocationGroup';
import EditBudgetGroup from './components/features/EditBudgetGroup';
import EditDistributorList from './components/features/EditDistributorList';
import ImportAllocationGroupRelationships from './components/features/ImportAllocationGroupRelationships';
import ImportBudgetGroupRelationships from './components/features/ImportBudgetGroupRelationships';
import ImportDistributorGroupRelationships from './components/features/ImportDistributorGroupRelationships';
import LoginCallbackErrorHandler from './components/features/login/LoginCallbackErrorHandler';
import UserDetails from './components/features/UserDetails';
import UsersList from './components/features/UsersList';
import { ProtectedRoute } from './components/routing/ProtectedRoute';
import Footer from './components/shared/Footer/Footer';
import { SectionTitle } from './components/shared/Typography/Typography';
import WelcomeScreen from './components/shared/WelcomeScreen';
import { MainWrapper, SectionWrapper } from './components/shared/Wrappers/Wrappers';
import { buildInfo } from './config/buildInfo';
import RequireAuth from './contexts/auth/RequireAuth';
import { OrganizationProvider, useOrganizationId } from './contexts/OrganizationContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { useCurrentReleaseVersion } from './hooks/useCurrentReleaseVersion';
import { ROUTES } from './routes';
import { NEXUS_ROLES } from './shared/constants/roles';
import theme from './theme';
import ActionToolbar from './widgets/actionToolbar';
import Navbar from './widgets/navbar';
import AdminNavbar from './widgets/navbar/AdminNavbar';

const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      networkMode: 'always', // avoid unexpected offline errors when changing data.
    },
  },
  /* Automatic query invalidations after mutations - helps to avoid fine-grained invalidation after each mutations, sometimes we
  may forget what needs to be revalidated
  More on the topic: https://tkdodo.eu/blog/automatic-query-invalidation-after-mutations
  */
  mutationCache: new MutationCache({
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  }),
});

export { ROUTES } from './routes';

interface RouteConfig {
  path: string;
  component: ReactElement;
  title?: string;
  titleInline?: boolean;
  testId?: string;
}

const getAdminRouteConfigs = () => [
  {
    path: ROUTES.ADMIN_ORGANIZATION,
    component: <OrganizationManagement />,
    title: 'Manage Clients for Account Admin',
    testId: 'manage-clients-for-account-admin',
  },
  {
    path: ROUTES.ADMIN_ORGANIZATION_ADD,
    component: <AddOrganization />,
  },
  {
    path: ROUTES.ADMIN_ORGANIZATION_DETAILS,
    component: <OrganizationDetails />,
  },
];

const getRouteConfigs = (): RouteConfig[] => [
  {
    path: ROUTES.HOME,
    component: <UsersList />,
    title: 'User Dashboard',
    testId: 'dashboard-page-label',
  },
  {
    path: ROUTES.USERS,
    component: <UsersList />,
    title: 'User Dashboard',
    testId: 'user-page-label',
  },
  {
    path: ROUTES.USER_DETAILS,
    component: <UserDetails />,
  },
  {
    path: ROUTES.ALLOCATIONS,
    component: <AllocationsGroupList />,
    title: 'Inventory Allocations',
    testId: 'allocation-page-label',
  },
  {
    path: ROUTES.ALLOCATIONS_MANAGE,
    component: <EditAllocationGroup />,
  },
  {
    path: ROUTES.BUDGETS,
    component: <BudgetGroupsList />,
    title: 'Budgets',
    testId: 'budget-page-label',
  },
  {
    path: ROUTES.BUDGETS_MANAGE,
    component: <EditBudgetGroup />,
  },
  {
    path: ROUTES.DISTRIBUTORS,
    component: <DistributorsList />,
    title: 'Distributors',
    testId: 'distributor-page-label',
  },
  {
    path: ROUTES.DISTRIBUTORS_MANAGE,
    component: <EditDistributorList />,
  },
  {
    path: ROUTES.AUDIT,
    component: <AuditLogsList />,
    title: 'Audit',
    titleInline: true,
    testId: 'audit-page-label',
  },
  {
    path: ROUTES.IMPORT_DISTRIBUTOR_GROUP_RELATIONSHIPS,
    component: <ImportDistributorGroupRelationships />,
    title: 'Import Distributor Group Relationships',
    testId: 'import-distributor-page-label',
  },
  {
    path: ROUTES.IMPORT_ALLOCATION_GROUP_RELATIONSHIPS,
    component: <ImportAllocationGroupRelationships />,
    title: 'Import Inventory Allocation Relationships',
    testId: 'import-allocation-page-label',
  },
  {
    path: ROUTES.IMPORT_BUDGET_GROUP_RELATIONSHIPS,
    component: <ImportBudgetGroupRelationships />,
    title: 'Import Budget Group Relationships',
    testId: 'import-budget-page-label',
  },
];

const renderRouteElement = (config: RouteConfig): ReactElement => {
  const { component, title, titleInline, testId } = config;

  return (
    <SectionWrapper>
      {title && !titleInline && <SectionTitle data-testid={testId}>{title}</SectionTitle>}
      {titleInline && title ? cloneElement(component, { title, titleTestId: testId } as Record<string, unknown>) : component}
    </SectionWrapper>
  );
};

function AppLayout() {
  const location = useLocation();
  const { organizationId } = useOrganizationId();
  const initialOrganizationIdRef = useRef<number>(organizationId);
  const navigate = useNavigate();

  // Check if we're in an admin route
  const isAdminRoute = location.pathname.startsWith('/admin');

  const isWelcomeScreenVisible = !isAdminRoute && organizationId === 0;

  const { data: currentRelease, error: currentReleaseError } = useCurrentReleaseVersion();

  const routeConfigs = getRouteConfigs();
  const adminRouteConfigs = getAdminRouteConfigs();

  // Go home when organizationId changes
  useEffect(() => {
    if (organizationId !== initialOrganizationIdRef.current) {
      navigate('/', { replace: true });
      initialOrganizationIdRef.current = organizationId;
    }
  }, [organizationId, navigate, location.pathname]);

  return (
    <Box className='app'>
      {isAdminRoute ? <AdminNavbar /> : <Navbar />}
      <MainWrapper component='main'>
        {isWelcomeScreenVisible ? (
          <WelcomeScreen />
        ) : (
          <>
            {!isAdminRoute && <ActionToolbar />}
            <Routes>
              {/* Admin Manager only routes - organization agnostic */}
              {adminRouteConfigs.map((config) => (
                <Route key={config.path} path={config.path} element={<ProtectedRoute requiredRole={NEXUS_ROLES.ADMIN_MANAGER} />}>
                  <Route index element={renderRouteElement(config)} />
                </Route>
              ))}

              {/* Organization-specific routes - dynamically generated */}
              {routeConfigs.map((config) => (
                <Route key={config.path} path={config.path} element={renderRouteElement(config)} />
              ))}
            </Routes>
          </>
        )}
      </MainWrapper>
      <Footer
        uiVersion={buildInfo.version}
        apiVersion={currentRelease?.version}
        apiError={currentReleaseError}
        legalInformation='All rights reserved.'
      />
    </Box>
  );
}

function ProtectedAppContent() {
  return (
    <Routes>
      <Route path={ROUTES.LOGIN_CALLBACK} element={<LoginCallback errorComponent={LoginCallbackErrorHandler} />} />
      <Route
        path='*'
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <CssBaseline />
        <SnackbarProvider>
          <OrganizationProvider>
            <ProtectedAppContent />
          </OrganizationProvider>
        </SnackbarProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
